/**
 * CapsuleAudioControl — the capsule video/quiz volume control, shared by
 * every capsule screen: CapsulePlay (both presentations), the quiz question
 * slides, and the immersive video feed.
 *
 * ONE component, ONE silhouette:
 *  - Closed it is a round mute/unmute button.
 *  - Open it grows into a single capsule whose right cap is the mute icon and
 *    whose body is the volume line — the icon is NOT a separate circle glued
 *    to a square-sided pill, so there is no seam, no protruding corner and no
 *    "circle with a rectangular side".
 *  - The expand/collapse is animated: the pill slides out of the button to
 *    the left and tucks back in, all on one shared surface + one border.
 *
 * Interaction model (one unioned piece, one button, two functions):
 *  - The circular icon ALWAYS toggles mute on press (single click works) and
 *    peeks the pill open for a moment so the volume is visibly adjustable.
 *  - Web: hovering the piece keeps the pill open; leaving closes it.
 *  - Dragging the line (or tapping it) sets the volume; muting at 0.
 *  - Minimal by design: no percentage text.
 *
 * Single source of truth: `lib/sounds` master mute/volume. No new audio state
 * lives here — the component reads and writes the same module state the quiz
 * uses, so whatever the user sets while watching a capsule is what the quiz
 * music/SFX inherit when it opens (and vice versa).
 *
 * Renders as a full-size, box-none overlay so it can float the button and the
 * (expanding) pill above the content around it. Consumers place it last in
 * their tree and give it the button's top/right offsets.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {
  Animated,
  Easing,
  PanResponder,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'

import {
  getMasterVolume,
  getMuted,
  setMasterVolume,
  setMuted,
} from '#/lib/sounds'
import {IS_WEB} from '#/env'

/** Outer size of the closed circular button (includes the 1px border). */
const BTN_OUTER = 42
/** Radius of the capsule ends — half the height, so closed it is a circle. */
const RADIUS = BTN_OUTER / 2
/** Inner width the pill expands into (content box of the capsule body). */
const PANEL_WIDTH = 128
const OPEN_MS = 200
const CLOSE_MS = 160
/** How long a press-opened pill stays up when there is no hover to hold it. */
const PRESS_PEEK_MS = 2200

/** Shared translucent surface — one fill for the whole capsule. */
const SURFACE = 'rgba(22, 27, 34, 0.94)'
const BORDER = 'rgba(255, 255, 255, 0.14)'
/** Volume line + thumb geometry (slim line, compact thumb). */
const TRACK_H = 4
const THUMB_SIZE = 16
const TRACK_INSET_LEFT = RADIUS - 2 // start the line just past the left cap
const TRACK_INSET_RIGHT = 4

type OpenReason = 'hover' | 'press' | null

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

export function CapsuleAudioControl({
  primaryColor,
  buttonTop,
  buttonRight = 16,
  displayMuted,
  onStateChange,
}: {
  primaryColor: string
  /** Screen-relative top of the toggle button. */
  buttonTop: number
  /** Screen-relative right of the toggle button. */
  buttonRight?: number
  /**
   * The host's knowledge of the actual player mute state, overriding the
   * floating icon when it differs from the module state (e.g. a video page
   * that starts muted by policy while the master volume is live). The panel
   * itself always edits the shared module state. Once the user interacts,
   * the master state wins (the host mirrors it via onStateChange).
   */
  displayMuted?: boolean
  /** Called after every user change so hosts can mirror onto their player. */
  onStateChange?: (state: {muted: boolean; volume: number}) => void
}) {
  const [open, setOpen] = useState(false)
  const [volDisplay, setVolDisplay] = useState(getMasterVolume())
  const [mutedDisplay, setMutedDisplay] = useState(getMuted())
  const [userTouched, setUserTouched] = useState(false)
  const showMuted = userTouched ? mutedDisplay : displayMuted ?? mutedDisplay
  const trackWidthRef = useRef(0)
  // 0 = closed circle, 1 = fully expanded capsule. One value drives the wrap
  // width, the pill width and the pill content opacity, so expand and collapse
  // stay perfectly in sync on every platform.
  const openProg = useRef(new Animated.Value(0)).current
  const openReasonRef = useRef<OpenReason>(null)
  const peekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelPeek = useCallback(() => {
    if (peekTimerRef.current) {
      clearTimeout(peekTimerRef.current)
      peekTimerRef.current = null
    }
  }, [])

  // Expand/collapse animation (width + content fade). Layout props animate on
  // the JS thread — this is a tiny control, so it stays smooth everywhere.
  useEffect(() => {
    Animated.timing(openProg, {
      toValue: open ? 1 : 0,
      duration: open ? OPEN_MS : CLOSE_MS,
      easing: open ? Easing.out(Easing.cubic) : Easing.in(Easing.cubic),
      useNativeDriver: false,
    }).start()
  }, [open, openProg])

  // Re-sync the panel from the shared source whenever it opens — another
  // control (e.g. the quiz, or the play screen) may have changed it.
  useEffect(() => {
    if (!open) return
    setVolDisplay(getMasterVolume())
    setMutedDisplay(getMuted())
  }, [open])

  useEffect(() => cancelPeek, [cancelPeek])

  /**
   * Open the pill and keep it open:
   *  - while the pointer hovers the piece (web), or
   *  - for a short peek after a press (touch/no-hover) so the volume is
   *    reachable without a hover. A hover open is never demoted to a peek:
   *    a mouse user who clicks mute must not lose the pill under the cursor.
   */
  const openPanel = useCallback(
    (reason: Exclude<OpenReason, null>) => {
      const prev = openReasonRef.current
      openReasonRef.current =
        reason === 'hover' || prev === 'hover' ? 'hover' : reason
      setOpen(true)
      cancelPeek()
      if (openReasonRef.current === 'press') {
        peekTimerRef.current = setTimeout(() => {
          openReasonRef.current = null
          setOpen(false)
        }, PRESS_PEEK_MS)
      }
    },
    [cancelPeek],
  )

  const closePanel = useCallback(() => {
    openReasonRef.current = null
    cancelPeek()
    setOpen(false)
  }, [cancelPeek])

  // A press works on the FIRST click: it flips the state the user actually
  // SEES, then peeks the pill open so the volume is adjustable. Toggling the
  // master flag alone would be a silent no-op under the start-muted policy
  // (element muted while the master flag reads "unmuted") — the first tap
  // would mute the master, change nothing audible and keep the ✕ icon up.
  const onButtonPress = useCallback(() => {
    const next = !showMuted
    setMutedDisplay(next)
    setMuted(next)
    setUserTouched(true)
    // While muting, drop the applied volume too so a later unmute restores a
    // clearly audible level from the untouched master volume.
    onStateChange?.({muted: next, volume: next ? 0 : getMasterVolume()})
    openPanel('press')
  }, [showMuted, onStateChange, openPanel])

  const applyRatio = useCallback(
    (rawRatio: number) => {
      const ratio = Math.max(0, Math.min(1, rawRatio))
      setVolDisplay(ratio)
      setMasterVolume(ratio)
      if (ratio <= 0) {
        // Dragging all the way down mutes.
        setMutedDisplay(true)
        setMuted(true)
      } else if (getMuted()) {
        setMutedDisplay(false)
        setMuted(false)
      }
      setUserTouched(true)
      onStateChange?.({muted: getMuted(), volume: ratio})
    },
    [onStateChange],
  )

  const volPanResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: e => {
        // Keep the pill up while dragging, whatever opened it.
        cancelPeek()
        applyRatio(
          e.nativeEvent.locationX / Math.max(trackWidthRef.current, 1),
        )
      },
      onPanResponderMove: e => {
        cancelPeek()
        applyRatio(
          e.nativeEvent.locationX / Math.max(trackWidthRef.current, 1),
        )
      },
      onPanResponderRelease: () => {
        if (openReasonRef.current === 'press') openPanel('press')
      },
      onPanResponderTerminate: () => {
        if (openReasonRef.current === 'press') openPanel('press')
      },
    }),
  ).current

  const animWidth = openProg.interpolate({
    inputRange: [0, 1],
    outputRange: [BTN_OUTER, BTN_OUTER + PANEL_WIDTH],
  })
  const pillWidth = openProg.interpolate({
    inputRange: [0, 1],
    outputRange: [0, PANEL_WIDTH],
  })

  return (
    // The root is a full-screen box-none overlay; an explicit zIndex keeps
    // the whole control (and its wrap) above the host page's chrome — on web
    // RNW views are positioned with z-index:0, which stacks the control BELOW
    // overlays like the capsule front (z20) and transport layer (z10) and
    // makes the mute button unreachable by real clicks. 60 clears every
    // in-page sibling (highest is the Play CTA at 50); screen-level overlays
    // (quiz z40, prompt z60) are siblings of the pager and still paint above.
    <View
      pointerEvents="box-none"
      style={[StyleSheet.absoluteFill, {zIndex: 60}]}>
      {/* One capsule: closed = circle (button), open = button + pill on a
          single shared surface with one rounded outline. The wrap is
          right-anchored, so growing the width extends the pill to the LEFT —
          it slides out from under the button. */}
      <AnimatedPressable
        accessibilityRole="none"
        focusable={false}
        pointerEvents={IS_WEB ? 'auto' : 'box-none'}
        onHoverIn={() => openPanel('hover')}
        onHoverOut={closePanel}
        style={[
          ss.wrap,
          {
            top: buttonTop,
            right: buttonRight,
            width: animWidth,
          },
        ]}>
        {/* Volume pill — expands/contracts under the same openProg value. */}
        <Animated.View
          style={[
            ss.pill,
            {
              width: pillWidth,
              opacity: openProg.interpolate({
                inputRange: [0, 0.001, 0.5, 1],
                outputRange: [0, 0, 0.72, 1],
              }),
            },
          ]}>
          <View
            style={ss.trackArea}
            onLayout={e => {
              trackWidthRef.current = e.nativeEvent.layout.width
            }}
            {...volPanResponder.panHandlers}>
            {/* Slim track — a thin line, not a fat bar. */}
            <View style={ss.volTrack} />
            <View
              style={[
                ss.volFill,
                {
                  width: `${Math.round(volDisplay * 100)}%`,
                  backgroundColor: primaryColor,
                },
              ]}
            />
            <View
              style={[
                ss.volThumb,
                {
                  left: `${Math.round(volDisplay * 100)}%`,
                  borderColor: primaryColor,
                },
              ]}
            />
          </View>
        </Animated.View>

        {/* Mute icon — the capsule's right cap (same surface, no seam). */}
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={showMuted ? 'Unmute audio' : 'Mute audio'}
          accessibilityHint="Toggles sound; the slider sets the volume"
          onPress={onButtonPress}
          style={ss.volToggleBtn}>
          <Feather
            name={showMuted ? 'volume-x' : 'volume-2'}
            size={18}
            color="rgba(255,255,255,0.94)"
          />
        </TouchableOpacity>
      </AnimatedPressable>
    </View>
  )
}

const ss = StyleSheet.create({
  wrap: {
    position: 'absolute',
    height: BTN_OUTER,
    borderRadius: RADIUS,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: SURFACE,
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
    zIndex: 100,
  },
  // The pill body is transparent — the wrap's own surface shows through, so
  // there is exactly one fill and one outline for button + pill.
  pill: {
    overflow: 'hidden',
  },
  trackArea: {
    flex: 1,
    marginLeft: TRACK_INSET_LEFT,
    marginRight: TRACK_INSET_RIGHT,
    position: 'relative',
    justifyContent: 'center',
  },
  volTrack: {
    height: TRACK_H,
    borderRadius: TRACK_H / 2,
    backgroundColor: 'rgba(255,255,255,0.20)',
    width: '100%',
  },
  volFill: {
    position: 'absolute',
    left: 0,
    height: TRACK_H,
    borderRadius: TRACK_H / 2,
    top: '50%',
    marginTop: -TRACK_H / 2,
  },
  volThumb: {
    position: 'absolute',
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    top: '50%',
    marginTop: -THUMB_SIZE / 2,
    marginLeft: -THUMB_SIZE / 2,
    borderWidth: 2,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOpacity: 0.35,
    shadowRadius: 4,
    shadowOffset: {width: 0, height: 2},
    elevation: 4,
  },
  volToggleBtn: {
    width: BTN_OUTER - 2,
    height: BTN_OUTER - 2,
    borderRadius: (BTN_OUTER - 2) / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
})
